import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Copy, Share2, Users, DollarSign, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { fmt } from "@/lib/format";

export default function Referrals() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/referrals").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-8 text-gray-500">Loading...</div>;

  const copy = (txt, label) => {
    navigator.clipboard.writeText(txt);
    toast.success(`${label} copied`);
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on Blockchain Properties",
          text: `I'm investing in tokenized real estate on Blockchain Properties. Use my code ${data.code} and get ${fmt(data.referee_reward)} to kick-start your portfolio.`,
          url: data.link,
        });
      } catch (err) {
        if (err?.name !== "AbortError") logger.warn("Share cancelled or failed:", err?.message);
      }
    } else {
      copy(data.link, "Link");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-5 h-5 text-[#D4AF37]" />
          <div className="overline">Broker dashboard</div>
          {data.is_affiliate && (
            <span
              data-testid="affiliate-badge"
              className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40"
            >
              Verified Broker
            </span>
          )}
        </div>
        <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mb-3">
          Earn on every brick.
        </h1>
        <p className="text-gray-400 max-w-2xl mb-10">
          Share your code. Earn <span className="text-[#D4AF37] font-medium">{fmt(data.affiliate_commission_per_brick)}</span> commission for <em>every single brick</em> your referred investors buy — first purchase and every purchase after. No cap.
        </p>

        {/* Rewards explanation */}
        <div className="grid md:grid-cols-2 gap-4">
          <RewardCard
            icon={DollarSign}
            amount={data.affiliate_commission_per_brick}
            title="Commission per brick"
            subtitle="Credited to your wallet on every brick your referrals buy — every purchase, every time."
            accent
          />
          <div className="bp-card rounded-2xl p-6 flex gap-4 items-start">
            <div className="w-11 h-11 rounded-full bg-white/5 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-gray-400" />
            </div>
            <div>
              <div className="overline">Unlimited invites</div>
              <div className="font-heading text-2xl text-white font-light mt-1">No cap on referrals</div>
              <div className="text-xs text-gray-400 mt-1 leading-relaxed">
                Invite as many investors as you want. Every one of their bricks pays you out.
              </div>
            </div>
          </div>
        </div>
        {/* Share box */}
        <div className="bp-card rounded-2xl p-6 md:p-8 mt-6">
          <div className="overline mb-3">Your referral code</div>
          <div className="flex items-center gap-3 flex-wrap">
            <div
              className="font-mono text-3xl md:text-5xl text-[#D4AF37] font-light tracking-widest py-4 px-6 rounded-xl bg-[#050914] border border-[#D4AF37]/30"
              data-testid="referral-code"
            >
              {data.code}
            </div>
            <Button
              variant="outline"
              className="btn-gold-outline rounded-full h-11"
              onClick={() => copy(data.code, "Code")}
              data-testid="copy-code-btn"
            >
              <Copy className="w-4 h-4 mr-1.5" /> Copy code
            </Button>
          </div>

          <div className="mt-5">
            <div className="overline mb-2">Shareable link</div>
            <div className="flex items-center gap-3 flex-wrap">
              <input
                readOnly
                value={data.link}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-[200px] bg-[#050914] border border-white/10 rounded-lg px-3 h-11 text-sm text-gray-300 font-mono focus:border-[#D4AF37] focus:outline-none"
                data-testid="referral-link"
              />
              <Button
                className="btn-gold rounded-full h-11"
                onClick={share}
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4 mr-1.5" /> Share
              </Button>
              <Button
                variant="outline"
                className="rounded-full h-11"
                onClick={() => copy(data.link, "Link")}
                data-testid="copy-link-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <StatTile icon={Users} label="Total signups" value={data.total_signups} />
          <StatTile icon={Sparkles} label="Buyers" value={data.completed_signups} />
          <StatTile icon={Users} label="Bricks sold" value={data.total_bricks_sold || 0} />
          <StatTile
            icon={DollarSign}
            label="Earned"
            value={fmt(data.total_earned)}
            accent
          />
        </div>

        {/* List */}
        <div className="mt-8">
          <div className="overline mb-4">Your referrals</div>
          <div className="bp-surface rounded-2xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="overline">Name</TableHead>
                  <TableHead className="overline">Email</TableHead>
                  <TableHead className="overline">KYC</TableHead>
                  <TableHead className="overline">Earned</TableHead>
                  <TableHead className="overline">Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.signups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10 text-gray-500">
                      No signups yet — share your link to start earning.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.signups.map((s) => (
                    <TableRow key={s.id} className="border-white/5" data-testid={`referral-row-${s.id}`}>
                      <TableCell className="text-white">{s.name}</TableCell>
                      <TableCell className="text-gray-400 text-sm">{s.email}</TableCell>
                      <TableCell><KycPill status={s.kyc_status} /></TableCell>
                      <TableCell>
                        {(s.commission_earned || 0) > 0 ? (
                          <span className="text-[#D4AF37] font-mono text-sm">
                            +{fmt(s.commission_earned)}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">Pending purchase</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {new Date(s.joined_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function RewardCard({ icon: Icon, amount, title, subtitle, accent }) {
  return (
    <div
      className={`bp-card rounded-2xl p-6 flex gap-4 ${
        accent ? "border-[#D4AF37]/40" : ""
      }`}
    >
      <div
        className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
          accent ? "bg-[#D4AF37]/15 border border-[#D4AF37]/40" : "bg-white/5"
        }`}
      >
        <Icon className={`w-5 h-5 ${accent ? "text-[#D4AF37]" : "text-gray-300"}`} />
      </div>
      <div>
        <div className="overline">{title}</div>
        <div
          className={`font-heading text-4xl mt-1 font-light ${accent ? "text-[#D4AF37]" : "text-white"}`}
        >
          {fmt(amount)}
        </div>
        <div className="text-xs text-gray-400 mt-1 leading-relaxed">{subtitle}</div>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, accent }) {
  return (
    <div className={`bp-surface rounded-2xl p-5 ${accent ? "border-[#D4AF37]/30" : ""}`}>
      <Icon className={`w-4 h-4 mb-3 ${accent ? "text-[#D4AF37]" : "text-gray-400"}`} />
      <div className="overline text-[10px]">{label}</div>
      <div
        className={`font-heading text-2xl md:text-3xl mt-2 font-light ${accent ? "text-[#D4AF37]" : "text-white"}`}
      >
        {value}
      </div>
    </div>
  );
}

function KycPill({ status }) {
  const map = {
    none: { c: "#6B7280", label: "Not started" },
    pending: { c: "#F59E0B", label: "Pending" },
    approved: { c: "#10B981", label: "Verified" },
    rejected: { c: "#EF4444", label: "Rejected" },
  };
  const s = map[status] || map.none;
  return (
    <span
      className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full"
      style={{ color: s.c, background: `${s.c}15`, border: `1px solid ${s.c}40` }}
    >
      {s.label}
    </span>
  );
}
